import {
  AppBar,
  Divider,
  Drawer,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Popover,
  Toolbar,
  Typography,
} from "@material-ui/core";
import { RouteComponentProps, useLocation } from "@reach/router";
import clsx from "clsx";
import * as React from "react";
import { SessionLoadingIndicator } from "../components/SessionLoadingIndicator";
import { useUserSession } from "../lib/session";
import { Link } from "@reach/router";
import Menu from "@material-ui/icons/Menu";
import KeyboardIcon from "@material-ui/icons/Keyboard";
import { Routes } from "../routes";
import { Helmet } from "react-helmet";
import { useHotkeys } from "react-hotkeys-hook";
import { Api } from "../lib/api";

const drawerWidth = 215;
const appBarHeight = 48;

const useStyles = makeStyles((theme) => ({
  link: {
    textDecoration: "none",
    color: "initial",
  },
  root: {
    display: "flex",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    top: appBarHeight,
    height: "calc(100vh - 64px)",
  },
  content: {
    overflow: "auto",
    flexGrow: 1,
    maxWidth: `calc(100vw - 20px)`,
    padding: theme.spacing(2),
    paddingTop: 5,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    maxWidth: `calc(100vw - ${drawerWidth}px)`,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  popover: {
    pointerEvents: "none",
  },
  paper: {
    padding: theme.spacing(1),
  },
}));

const listStyles = makeStyles((theme) => ({
  root: {
    background: "#E9F1FF",
  },
  listItem: {
    paddingBottom: 12,
    paddingTop: 12,
  },
  nested: {
    paddingLeft: theme.spacing(4),
    background: "rgba(255, 255, 255, 1)",
    paddingBottom: 12,
    paddingTop: 12,
  },
}));

export interface LayoutProps extends RouteComponentProps {
  title: string;
  /**
   * Whether the menu should be open initially. Defaults to true.
   */
  menuOpen?: boolean;
}

export const Layout: React.FC<LayoutProps> = (props) => {
  const { session } = useUserSession();

  const classes = useStyles();

  const liststyles = listStyles();

  const location = useLocation();

  const [open, setOpen] = React.useState(
    props.menuOpen !== undefined ? props.menuOpen : true,
  );

  const opts: any = {
    enableOnTags: ["INPUT", "TEXTAREA", "SELECT"],
  };

  useHotkeys("ctrl+b", (e) => setOpen(!open), opts);
  useHotkeys("⌘+b", (e) => setOpen(!open), opts);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const performQuckResolve = async (value: string) => {
    const res = await Api.resolve(value);
    const { user, root, website } = res.data as any;

    if (root) {
      window.location.href = `/user/${user}#Root${root}`;
    } else {
      window.location.href = `/user/${user}`;
    }
  };

  const handleQuickResolveKeyDown: React.KeyboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (event) => {
    if (event.key === "Enter") {
      performQuckResolve(event.currentTarget.value);
    }
  };

  const popoverOver = Boolean(anchorEl);

  //if (!counts) return <></>;

  return (
    <SessionLoadingIndicator session={session}>
      <AppBar position="fixed">
        <Toolbar variant="dense">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setOpen(!open)}
            edge="start"
          >
            <Menu />
          </IconButton>
          <Helmet>
            <title>{props.title}</title>
          </Helmet>
          <Typography variant="h6" noWrap>
            {props.title}
          </Typography>
          <div style={{ width: "100px", marginLeft: "auto" }}>
            <div
              style={{ display: "flex" }}
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            >
              <Typography style={{ marginRight: "10px" }}>Shortcuts</Typography>
              <KeyboardIcon />
            </div>
            <Popover
              id="mouse-over-popover"
              className={classes.popover}
              classes={{
                paper: classes.paper,
              }}
              open={popoverOver}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              onClose={handlePopoverClose}
              disableRestoreFocus
            >
              <ul style={{ paddingLeft: 10 }}>
                <li>ctrl+b ---&gt; Toggle menu open/close</li>
                <li>ctrl+q ---&gt; Create new parent source</li>
                <li>ctrl+s (mouse inside a source) ---&gt; Save source</li>
                <li>ctrl+d (mouse inside a source) ---&gt; Expand source</li>
                <li>
                  ctrl+shift+q (mouse inside a source) ---&gt; Create new child
                  website
                </li>
              </ul>
            </Popover>
          </div>
        </Toolbar>
      </AppBar>
      <div className={classes.root} style={{ marginTop: "64px" }}>
        <Drawer
          className={classes.drawer}
          variant="persistent"
          anchor="left"
          open={open}
          classes={{
            paper: classes.drawerPaper,
          }}
        >
          <List className={liststyles.root} disablePadding>
            <Link to={Routes.NewUsers} className={classes.link}>
              <ListItem
                button
                className={liststyles.listItem}
                selected={location.pathname === Routes.NewUsers}
              >
                <ListItemText>New users - to be cleaned</ListItemText>
              </ListItem>
            </Link>
            <Divider />
            <Link to={Routes.AllActiveUsers} className={classes.link}>
              <ListItem
                className={liststyles.listItem}
                button
                selected={location.pathname === Routes.AllActiveUsers}
              >
                <ListItemText>Active</ListItemText>
              </ListItem>
            </Link>
            <Divider />
            <Link to={Routes.SourceTuning} className={classes.link}>
              <ListItem
                className={liststyles.nested}
                button
                selected={location.pathname === Routes.SourceTuning}
              >
                <ListItemText>
                  <Typography variant="body2">Source Tuning</Typography>
                </ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.ActiveNewWebsite} className={classes.link}>
              <ListItem
                className={liststyles.nested}
                button
                selected={location.pathname === Routes.ActiveNewWebsite}
              >
                <ListItemText>
                  <Typography variant="body2">Added Website</Typography>
                </ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.AwaitingFeedback} className={classes.link}>
              <ListItem
                className={liststyles.nested}
                button
                selected={location.pathname === Routes.AwaitingFeedback}
              >
                <ListItemText>
                  <Typography variant="body2">Awaiting Feedback</Typography>
                </ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.ActiveFullCrawl} className={classes.link}>
              <ListItem
                className={liststyles.nested}
                button
                selected={location.pathname === Routes.ActiveFullCrawl}
              >
                <ListItemText>
                  <Typography variant="body2">In Progress</Typography>
                </ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.ActiveImportFinished} className={classes.link}>
              <ListItem
                className={liststyles.nested}
                button
                selected={location.pathname === Routes.ActiveImportFinished}
              >
                <ListItemText>
                  <Typography variant="body2">Act Import Finished</Typography>
                </ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.ContCraw} className={classes.link}>
              <ListItem
                className={liststyles.nested}
                button
                selected={location.pathname === Routes.ContCraw}
              >
                <ListItemText>
                  <Typography variant="body2">Cont. Crawl</Typography>
                </ListItemText>
              </ListItem>
            </Link>
            <Divider />
            <Link to={Routes.MarkedForDowngrade} className={classes.link}>
              <ListItem
                className={liststyles.listItem}
                button
                selected={location.pathname === Routes.MarkedForDowngrade}
              >
                <ListItemText>Marked for downgrade</ListItemText>
              </ListItem>
            </Link>
            <Divider />
            <Link to={Routes.InactiveUser} className={classes.link}>
              <ListItem
                className={liststyles.listItem}
                button
                selected={location.pathname === Routes.InactiveUser}
              >
                <ListItemText>Inactive</ListItemText>
              </ListItem>
            </Link>
            <Divider />
            <Link to="/dashboard" className={classes.link}>
              <ListItem
                className={liststyles.listItem}
                button
                selected={location.pathname.startsWith(Routes.Dashboard)}
              >
                <ListItemText>Dashboard</ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.URl} className={classes.link}>
              <ListItem
                button
                className={liststyles.listItem}
                selected={location.pathname === Routes.URl}
              >
                <ListItemText>URLs</ListItemText>
              </ListItem>
            </Link>
            <Divider />
            <Link to={Routes.UserCounts} className={classes.link}>
              <ListItem
                className={liststyles.listItem}
                button
                selected={location.pathname.startsWith(Routes.UserCounts)}
              >
                <ListItemText>User counts</ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.Dashboard} className={classes.link}>
              <ListItem
                className={liststyles.listItem}
                button
                selected={location.pathname.startsWith(Routes.Dashboard)}
              >
                <ListItemText>Source Status</ListItemText>
              </ListItem>
            </Link>
            <Link to={Routes.ExpensiveSources} className={classes.link}>
              <ListItem
                className={liststyles.listItem}
                button
                selected={location.pathname.startsWith(Routes.ExpensiveSources)}
              >
                <ListItemText>Most Expensive Sources</ListItemText>
              </ListItem>
            </Link>
            <ListItem className={liststyles.listItem}>
              <ListItemText>
                <Input
                  type="text"
                  placeholder="Quick Resolve..."
                  onKeyDown={handleQuickResolveKeyDown}
                ></Input>
              </ListItemText>
            </ListItem>
          </List>
        </Drawer>
        <main
          className={clsx(classes.content, {
            [classes.contentShift]: open,
          })}
        >
          {props.children}
        </main>
      </div>
    </SessionLoadingIndicator>
  );
};
